import React, { useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { toast } from 'react-hot-toast';

const ATTRIBUTION_SOURCES = ['dynamic', 'first'];
const AD_SPEND_MODES = ['network', 'mixed', 'attribution', 'adjust'];
const REATTRIBUTED_OPTS = ['', 'all', 'true', 'false'];
const ATTRIBUTION_TYPES_OPTS = [
  '',
  'click',
  'impression',
  'engaged_ad',
  'click,impression',
  'click,impression,engaged_ad'
];
const DEFAULT_DIMENSIONS = 'campaign';
const DEFAULT_METRICS = 'installs,sessions,revenue,purchase_events,all_revenue';

const fmtNum = (v) => Number(v || 0).toLocaleString('tr-TR');
const fmtMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

function aggregateByCampaign(rows) {
  const map = new Map();
  for (const r of rows || []) {
    const key = r.campaign || '(no campaign)';
    const cur =
      map.get(key) ||
      {
        campaign: key,
        installs: 0,
        sessions: 0,
        purchase_events: 0,
        revenue: 0,
        all_revenue: 0
      };
    cur.installs += Number(r.installs || 0);
    cur.sessions += Number(r.sessions || 0);
    cur.purchase_events += Number(r.purchase_events || r.events || 0);
    cur.revenue += Number(r.revenue || 0);
    cur.all_revenue += Number(r.all_revenue || 0);
    map.set(key, cur);
  }
  return Array.from(map.values()).sort(
    (a, b) => b.all_revenue - a.all_revenue
  );
}

function totalsOf(rows) {
  return (rows || []).reduce(
    (acc, r) => {
      acc.installs += Number(r.installs || 0);
      acc.sessions += Number(r.sessions || 0);
      acc.purchase_events += Number(r.purchase_events || 0);
      acc.revenue += Number(r.revenue || 0);
      acc.all_revenue += Number(r.all_revenue || 0);
      return acc;
    },
    { installs: 0, sessions: 0, purchase_events: 0, revenue: 0, all_revenue: 0 }
  );
}

export default function AdjustAttributionCompare() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [utcOffset, setUtcOffset] = useState('+03:00');
  const [attributionSource, setAttributionSource] = useState('dynamic');
  const [adSpendMode, setAdSpendMode] = useState('network');
  const [reattributed, setReattributed] = useState('');
  const [attributionTypes, setAttributionTypes] = useState('');
  const [dimensions, setDimensions] = useState(DEFAULT_DIMENSIONS);
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);

  const [singleLoading, setSingleLoading] = useState(false);
  const [singleRows, setSingleRows] = useState([]);
  const [singleError, setSingleError] = useState(null);
  const [singleAttrUsed, setSingleAttrUsed] = useState(null);
  const [singleDebug, setSingleDebug] = useState([]);

  const [compareLoading, setCompareLoading] = useState(false);
  const [compareData, setCompareData] = useState({});

  const fetchOne = async (attrSource) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) throw new Error('Please select a brand first');
    const params = new URLSearchParams({
      dimensions,
      metrics,
      date_period: `${startDate}:${endDate}`,
      ad_spend_mode: adSpendMode,
      attribution_source: attrSource,
      utc_offset: utcOffset,
      format_dates: 'true'
    });
    if (reattributed) params.set('reattributed', reattributed);
    if (attributionTypes) params.set('attribution_types', attributionTypes);
    const res = await fetch(
      `https://api.glowytics.com/api/auth/adjust/report?${params.toString()}`,
      { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
    );
    const ct = res.headers.get('content-type') || '';
    if (!res.ok) {
      const err = ct.includes('application/json')
        ? await res.json().catch(() => ({}))
        : await res.text();
      throw new Error(
        (err && (err.error || err.details)) || String(err) || `HTTP ${res.status}`
      );
    }
    const data = ct.includes('application/json') ? await res.json() : {};
    const rows = Array.isArray(data.rows)
      ? data.rows
      : Array.isArray(data.data)
        ? data.data
        : [];
    return { rows, debug: Array.isArray(data.debug_requests) ? data.debug_requests : [] };
  };

  const handleFetchSingle = async () => {
    try {
      setSingleLoading(true);
      setSingleError(null);
      setSingleRows([]);
      setSingleDebug([]);
      const { rows, debug } = await fetchOne(attributionSource);
      setSingleRows(rows);
      setSingleAttrUsed(attributionSource);
      setSingleDebug(debug);
      toast.success(
        `Fetched ${rows.length} rows (attribution_source = ${attributionSource})`
      );
    } catch (e) {
      setSingleError(e.message || 'Failed');
      toast.error(e.message || 'Failed');
    } finally {
      setSingleLoading(false);
    }
  };

  const handleCompareAll = async () => {
    try {
      setCompareLoading(true);
      setCompareData({});
      const results = await Promise.all(
        ATTRIBUTION_SOURCES.map(async (src) => {
          try {
            const { rows, debug } = await fetchOne(src);
            return [src, { rows, debug }];
          } catch (e) {
            return [src, { error: e.message || 'Failed' }];
          }
        })
      );
      setCompareData(Object.fromEntries(results));
      toast.success('Comparison complete');
    } catch (e) {
      toast.error(e.message || 'Compare failed');
    } finally {
      setCompareLoading(false);
    }
  };

  const singleAgg = useMemo(() => aggregateByCampaign(singleRows), [singleRows]);
  const singleTotals = useMemo(() => totalsOf(singleAgg), [singleAgg]);

  const pivot = useMemo(() => {
    const all = new Map();
    for (const src of ATTRIBUTION_SOURCES) {
      const entry = compareData[src];
      if (!entry || entry.error) continue;
      const agg = aggregateByCampaign(entry.rows || []);
      for (const row of agg) {
        if (!all.has(row.campaign)) all.set(row.campaign, { campaign: row.campaign });
        all.get(row.campaign)[src] = row;
      }
    }
    const arr = Array.from(all.values());
    arr.sort((a, b) => {
      const maxA = Math.max(
        ...ATTRIBUTION_SOURCES.map((s) => Number(a[s]?.all_revenue || 0))
      );
      const maxB = Math.max(
        ...ATTRIBUTION_SOURCES.map((s) => Number(b[s]?.all_revenue || 0))
      );
      return maxB - maxA;
    });
    return arr;
  }, [compareData]);

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-xl font-semibold mb-1">
        Adjust — Campaign Revenue & Attribution Compare
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Live request to Adjust Reports Service. Pick an{' '}
        <code>attribution_source</code> or run the side-by-side comparison to see
        how campaign-level <code>all_revenue</code> changes across attribution
        models. Brand is taken from the current brand selector.
      </p>

      {/* Controls */}
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              max={endDate}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              min={startDate}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              UTC Offset
            </label>
            <input
              type="text"
              value={utcOffset}
              onChange={(e) => setUtcOffset(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attribution Source
            </label>
            <select
              value={attributionSource}
              onChange={(e) => setAttributionSource(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {ATTRIBUTION_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ad Spend Mode
            </label>
            <select
              value={adSpendMode}
              onChange={(e) => setAdSpendMode(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {AD_SPEND_MODES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              reattributed{' '}
              <span className="text-xs text-gray-500">(empty = Adjust default)</span>
            </label>
            <select
              value={reattributed}
              onChange={(e) => setReattributed(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {REATTRIBUTED_OPTS.map((s) => (
                <option key={s} value={s}>
                  {s === '' ? '(default)' : s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              attribution_types{' '}
              <span className="text-xs text-gray-500">(empty = Adjust default)</span>
            </label>
            <select
              value={attributionTypes}
              onChange={(e) => setAttributionTypes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {ATTRIBUTION_TYPES_OPTS.map((s) => (
                <option key={s} value={s}>
                  {s === '' ? '(default)' : s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dimensions
            </label>
            <input
              type="text"
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default <code>campaign</code> (aggregated across day/channel/os).
              Add more dims to drill down e.g. <code>campaign,os_name</code>.
            </p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Metrics
          </label>
          <input
            type="text"
            value={metrics}
            onChange={(e) => setMetrics(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            onClick={handleFetchSingle}
            disabled={singleLoading}
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-4"
          >
            {singleLoading
              ? 'Fetching…'
              : `Fetch (attribution_source = ${attributionSource})`}
          </Button>
          <Button
            onClick={handleCompareAll}
            disabled={compareLoading}
            className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl px-4"
          >
            {compareLoading
              ? 'Comparing all 4 sources…'
              : 'Compare all 4 attribution sources'}
          </Button>
        </div>
      </div>

      {/* Single fetch result */}
      <div className="bg-white rounded-xl border p-6 mt-6">
        <h3 className="text-lg font-medium mb-3">
          Campaign breakdown
          {singleAttrUsed && (
            <>
              {' '}
              — attribution:{' '}
              <span className="font-mono text-blue-700">{singleAttrUsed}</span>
            </>
          )}
        </h3>
        {singleDebug.length > 0 && (
          <details className="mb-3 text-xs">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
              Debug: actual Adjust API request URL{singleDebug.length > 1 ? 's' : ''}
            </summary>
            <div className="mt-2 space-y-1">
              {singleDebug.map((d, i) => (
                <div
                  key={i}
                  className="bg-gray-100 p-2 rounded font-mono text-xs break-all"
                >
                  <span className="text-green-700">[{d.status}]</span> {d.url}
                </div>
              ))}
            </div>
          </details>
        )}
        {singleError && (
          <div className="bg-red-50 text-red-700 border border-red-200 p-3 rounded mb-3 text-sm">
            {singleError}
          </div>
        )}
        {singleAgg.length === 0 ? (
          <p className="text-sm text-gray-500">
            {singleLoading
              ? 'Loading…'
              : 'No data. Click "Fetch" above to query Adjust.'}
          </p>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Campaign</th>
                  <th className="text-right p-2">Installs</th>
                  <th className="text-right p-2">Sessions</th>
                  <th className="text-right p-2">Purchases</th>
                  <th className="text-right p-2">IAP Revenue</th>
                  <th className="text-right p-2">All Revenue</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-yellow-50 font-semibold">
                  <td className="p-2">
                    TOTAL ({singleAgg.length} campaigns)
                  </td>
                  <td className="p-2 text-right">
                    {fmtNum(singleTotals.installs)}
                  </td>
                  <td className="p-2 text-right">
                    {fmtNum(singleTotals.sessions)}
                  </td>
                  <td className="p-2 text-right">
                    {fmtNum(singleTotals.purchase_events)}
                  </td>
                  <td className="p-2 text-right">
                    {fmtMoney(singleTotals.revenue)}
                  </td>
                  <td className="p-2 text-right">
                    {fmtMoney(singleTotals.all_revenue)}
                  </td>
                </tr>
                {singleAgg.slice(0, 500).map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="p-2">{r.campaign}</td>
                    <td className="p-2 text-right">{fmtNum(r.installs)}</td>
                    <td className="p-2 text-right">{fmtNum(r.sessions)}</td>
                    <td className="p-2 text-right">
                      {fmtNum(r.purchase_events)}
                    </td>
                    <td className="p-2 text-right">{fmtMoney(r.revenue)}</td>
                    <td className="p-2 text-right">{fmtMoney(r.all_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {singleAgg.length > 500 && (
              <p className="text-xs text-gray-500 mt-2">
                Showing first 500 of {singleAgg.length} campaigns
              </p>
            )}
          </div>
        )}
      </div>

      {/* Compare result */}
      <div className="bg-white rounded-xl border p-6 mt-6">
        <h3 className="text-lg font-medium mb-3">
          Side-by-side: All Revenue per Campaign × Attribution Source
        </h3>
        {Object.keys(compareData).length === 0 ? (
          <p className="text-sm text-gray-500">
            {compareLoading
              ? 'Loading 4 attribution sources in parallel…'
              : 'Click "Compare all 4 attribution sources" above.'}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {ATTRIBUTION_SOURCES.map((src) => {
                const entry = compareData[src];
                const agg =
                  entry && !entry.error
                    ? aggregateByCampaign(entry.rows || [])
                    : [];
                const t = totalsOf(agg);
                const dynTotals =
                  compareData.dynamic && !compareData.dynamic.error
                    ? totalsOf(aggregateByCampaign(compareData.dynamic.rows || []))
                    : null;
                const delta =
                  dynTotals && src !== 'dynamic'
                    ? t.all_revenue - dynTotals.all_revenue
                    : null;
                return (
                  <div
                    key={src}
                    className={`p-3 rounded-lg border ${
                      src === 'dynamic'
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="text-xs uppercase font-mono text-gray-700">
                      {src}
                    </div>
                    {entry?.error ? (
                      <div className="text-red-600 text-xs mt-1">
                        {entry.error}
                      </div>
                    ) : (
                      <>
                        <div className="text-lg font-semibold mt-1">
                          {fmtMoney(t.all_revenue)}
                        </div>
                        <div className="text-xs text-gray-600">
                          all_revenue • {fmtNum(t.purchase_events)} purchases
                        </div>
                        {delta != null && (
                          <div
                            className={`text-xs mt-1 ${
                              delta > 0 ? 'text-green-700' : delta < 0 ? 'text-red-700' : 'text-gray-500'
                            }`}
                          >
                            {delta > 0 ? '+' : ''}
                            {fmtMoney(delta)} vs dynamic
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {pivot.length === 0 ? (
              <p className="text-sm text-gray-500">No campaign rows.</p>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Campaign</th>
                      {ATTRIBUTION_SOURCES.map((s) => (
                        <th
                          key={s}
                          className={`text-right p-2 font-mono ${
                            s === 'dynamic' ? 'bg-blue-100' : ''
                          }`}
                        >
                          {s}
                          <br />
                          <span className="text-xs font-normal text-gray-500">
                            all_revenue
                          </span>
                        </th>
                      ))}
                      <th className="text-right p-2">Purchases (dyn)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pivot.slice(0, 500).map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="p-2">{row.campaign}</td>
                        {ATTRIBUTION_SOURCES.map((s) => (
                          <td
                            key={s}
                            className={`p-2 text-right ${
                              s === 'dynamic' ? 'bg-blue-50 font-medium' : ''
                            }`}
                          >
                            {fmtMoney(row[s]?.all_revenue || 0)}
                          </td>
                        ))}
                        <td className="p-2 text-right text-gray-600">
                          {fmtNum(row.dynamic?.purchase_events || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pivot.length > 500 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Showing first 500 of {pivot.length} campaigns
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
